import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: ('admin' | 'operador' | 'vendedor')[];
}

export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { session, roles, initializing, profileError, signOut } = useAuth();

  // Still loading
  if (initializing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  // Profile error (retry failed)
  if (profileError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <AlertTriangle className="w-12 h-12 text-accent" />
        <h1 className="text-lg font-semibold text-foreground">Preparando seu acesso...</h1>
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          Não foi possível carregar seu perfil. Tente fazer login novamente.
        </p>
        <Button 
          variant="outline" 
          onClick={() => signOut()}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  // Not authenticated
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check role permissions
  if (allowedRoles && allowedRoles.length > 0) {
    const hasPermission = allowedRoles.some(role => roles.includes(role));
    
    if (!hasPermission) {
      // Redirect to appropriate default route based on role
      if (roles.includes('admin')) {
        return <Navigate to="/admin" replace />;
      }
      if (roles.includes('operador')) {
        return <Navigate to="/app" replace />;
      }
      if (roles.includes('vendedor')) {
        return <Navigate to="/app" replace />;
      }
      // No recognized role - go to login
      return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};
